/**
 * `data_ops.get_item_groups_with_suppliers` — which suppliers each ITEM GROUP is mapped to.
 *
 * ---- What this is for ----
 *
 * An item has no suppliers of its own. `Item Group Supplier` is a child table of `Item Group`, so
 * an item's candidate suppliers are whatever its GROUP is mapped to. `items.get_suppliers_for_item`
 * does that join server-side and is the obvious way to ask — but it answers per item, one request
 * each, and it is a sibling of `items.get_blanket_orders`, which returns `[]` for items that
 * demonstrably have active orders (`D1` in `docs/bosun-frappe-asks.md` — a query bug, not missing
 * data). This method returns the whole mapping in ONE request, so the RFQ screen can resolve every
 * line's candidates from data it already holds (`Item.group`) without trusting that per-item join.
 *
 * It therefore runs as the PRIMARY source, with `useSuppliersForItem` as the fallback — see
 * `SupplierPickerModal.tsx`.
 *
 * ---- ⚠️ The response shape is UNVERIFIED ----
 *
 * This method arrived on 2026-08-05 in `Quoflow_DataOps.postman_collection.json`, whose 12
 * requests all ship an EMPTY `"response"` array. We know the request and nothing about the reply;
 * the bench has not been probed. So `toGroupSupplierMap` below accepts several plausible
 * envelopes rather than committing to one and silently returning nothing on live.
 *
 * That tolerance is a hedge against an unknown, NOT a design. Item 2 of
 * `docs/bosun-quoflow-followup-slack-2026-08-05.md` asks them to re-export with examples; when the
 * real shape lands, delete the branches that were wrong and narrow this to it.
 *
 * One thing it is deliberately strict about: **an absent `is_active` means ACTIVE.** The per-item
 * hook's `activeSupplierCodes` drops anything falsy, so a server that omits the field would
 * silently return no candidates at all — the exact failure this whole path exists to avoid.
 */

import { useQuery } from '@tanstack/react-query'
import { callGet } from './bosun'

/** How long the mapping is considered fresh. It is master data — it changes when someone edits an
 * `Item Group`, which is not something that happens mid-session. */
const GROUP_SUPPLIERS_STALE_MS = 10 * 60_000

/** `item_group` → the `Supplier.code`s mapped to it. Codes only — never display names. */
export type GroupSupplierMap = Map<string, string[]>

const isRecord = (v: unknown): v is Record<string, unknown> => typeof v === 'object' && v !== null && !Array.isArray(v)

/** Absent means active. Only an explicit `0`/`false` disables a mapping. */
function isActive(row: Record<string, unknown>): boolean {
  const flag = row.is_active
  if (flag === undefined || flag === null) return true
  return flag !== 0 && flag !== false && flag !== '0'
}

/** Whether this entry is recognisably a SUPPLIER at all — independent of whether it survives the
 * `is_active` filter. Counted separately from extracted codes so that a group whose mappings are
 * all disabled reads as "understood, none active" rather than "unparseable". */
function looksLikeSupplier(entry: unknown): boolean {
  if (typeof entry === 'string') return true
  return isRecord(entry) && ('supplier' in entry || 'name' in entry)
}

/** A supplier entry is either a bare docname string or a row carrying one. */
function supplierCode(entry: unknown): string {
  if (typeof entry === 'string') return entry.trim()
  if (!isRecord(entry)) return ''
  if (!isActive(entry)) return ''
  // `supplier` is the Link/docname. `supplier_name` is a LABEL and is never used as identity —
  // keying on a display name that happens to match is the single most-repeated bug of this
  // migration (see CLAUDE.md's API conventions).
  const code = entry.supplier ?? entry.name
  return typeof code === 'string' ? code.trim() : ''
}

/** Returns how many entries were RECOGNISED as suppliers (not how many survived filtering). */
function addRow(map: GroupSupplierMap, group: unknown, entries: unknown): number {
  const key = typeof group === 'string' ? group.trim() : ''
  if (key === '') return 0
  const list = Array.isArray(entries) ? entries : [entries]
  const recognised = list.filter(looksLikeSupplier).length
  if (recognised === 0) return 0
  const codes = list.map(supplierCode).filter((c) => c !== '')
  const bucket = map.get(key) ?? []
  map.set(key, [...new Set([...bucket, ...codes])])
  return recognised
}

/**
 * Parse whatever the method returned into `item_group` → `Supplier.code[]`.
 *
 * Accepted envelopes (see this module's header for why there are four):
 *   `{data: [{item_group, suppliers: [...]}]}`   — the shape the mock serves
 *   `[{item_group, suppliers: [...]}]`           — the same, unwrapped
 *   `{"<group>": [...]}`                          — a plain object map
 *   `{data: [{item_group, supplier}]}`            — flat rows, one supplier each
 */
export function toGroupSupplierMap(payload: unknown): GroupSupplierMap {
  const map: GroupSupplierMap = new Map()
  const body = isRecord(payload) && 'data' in payload ? payload.data : payload

  let recognised = 0

  if (Array.isArray(body)) {
    for (const row of body) {
      if (!isRecord(row)) continue
      // Flat form: the row IS one (group, supplier) pair.
      if ('suppliers' in row) recognised += addRow(map, row.item_group ?? row.name, row.suppliers)
      else if ('supplier' in row) recognised += addRow(map, row.item_group ?? row.name, row)
    }
  } else if (isRecord(body)) {
    for (const [group, entries] of Object.entries(body)) {
      if (Array.isArray(entries)) recognised += addRow(map, group, entries)
    }
  }

  // A genuinely empty mapping is normal (the bench maps 3.5% of groups). A payload that had
  // content we could not read is NOT — say so once, rather than presenting it as "no suppliers".
  const hadContent = Array.isArray(body) ? body.length > 0 : isRecord(body) && Object.keys(body).length > 0
  if (hadContent && recognised === 0) {
    console.warn(
      '[data_ops.get_item_groups_with_suppliers] response did not match any known shape — no item-group ' +
        'suppliers parsed. The collection ships no example response; see useItemGroupSuppliers.ts.',
      payload,
    )
  }

  return map
}

/**
 * The suppliers mapped to one group. An unmapped group returns `[]` — that is the COMMON case
 * (44 of 1,240 groups are mapped on the bench, and the Frappe team have confirmed the gaps are
 * permanent), not an error, and callers must render it as "none mapped" rather than "failed".
 */
export function suppliersForGroup(map: GroupSupplierMap | undefined, group: string | undefined): string[] {
  if (!map || !group) return []
  return map.get(group.trim()) ?? []
}

export function useItemGroupSuppliers() {
  return useQuery({
    queryKey: ['item-group-suppliers'] as const,
    queryFn: async () => toGroupSupplierMap(await callGet<unknown>('data_ops', 'get_item_groups_with_suppliers')),
    staleTime: GROUP_SUPPLIERS_STALE_MS,
  })
}
